import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil, Plus, Power, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FORM_CONTROL_FOCUS_WITHIN_CLASS } from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DatePicker } from '@/components/shared/DatePicker'
import { ConfirmDeleteDialog } from '@/components/shared'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { SettingsDrawerHeader } from '@/components/settings/SettingsDrawerHeader'
import { ListPagination } from '@/components/shared/ListPagination'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { useListParams } from '@/hooks/useListParams'
import {
  usePublicHolidays,
  useCreatePublicHoliday,
  useUpdatePublicHoliday,
  useDeletePublicHoliday,
} from '@/hooks/useSettings'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import i18n from '@/i18n'
import type { PublicHoliday, PublicHolidayRequest } from '@/types'

const COLUMN_COUNT = 6

const cardClass =
  'rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_rgba(38,45,66,0.05),0_8px_16px_-12px_rgba(38,45,66,0.1)]'

const thClass =
  'h-10 border-b border-[#e7e9ef] bg-[#fbfbfc] px-3 py-2 text-left text-[10px] font-medium uppercase tracking-[0.02em] text-[#3d434e]'

const tdClass = 'h-16 border-b border-[#e7e9ef] px-3 py-2 align-middle text-sm'

const actionBtnClass =
  'group/action inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] p-1.5 text-[#272c36] transition-colors group-hover:bg-[#fbfbfc] disabled:cursor-not-allowed disabled:opacity-50'
const accentActionBtnClass =
  'hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] group-hover:hover:bg-[#fff1ea]'
const destructiveActionBtnClass =
  'hover:border-[#d63b33] hover:bg-[#fde9e8] hover:text-[#d63b33] group-hover:hover:bg-[#fde9e8]'
const drawerClass =
  'tf-right-drawer-short min-h-0 max-h-dvh *:min-w-0 [&>button]:right-6 [&>button]:top-4 [&>button]:rounded-xl [&>button]:bg-[#f4f5f8] [&>button]:p-2 [&>button]:opacity-100 [&>button]:text-[#555b6a]'

const confirmDialogClass =
  'max-h-[90vh] w-[calc(100%-2rem)] max-w-[600px] gap-6 overflow-x-hidden overflow-y-auto border-[#e7e9ef] p-8 shadow-lg sm:rounded-2xl *:min-w-0 [&>button]:right-[18px] [&>button]:top-[18px] [&>button]:rounded-xl [&>button]:bg-[#f4f5f8] [&>button]:p-2 [&>button]:opacity-100 [&>button]:text-[#555b6a]'

const fieldLabelClass = 'text-xs font-medium leading-4 text-[#171a20]'

const fieldInputClass =
  'h-10 rounded-xl bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none placeholder:text-[#6c7285]'

const datePickerClass = cn(
  'rounded-xl bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] [&_input]:text-sm [&_input]:leading-[1.4]',
  FORM_CONTROL_FOCUS_WITHIN_CLASS
)

const drawerButtonOutlineClass =
  'rounded-xl border-[#e7e9ef] bg-white px-3 py-3 text-base font-medium text-[#171a20] shadow-none hover:bg-[#f8f9fb]'

const drawerButtonPrimaryClass =
  'rounded-xl bg-[#ff5a1f] px-3 py-3 text-base font-medium text-white shadow-none hover:bg-[#e6511c]'

const switchClass =
  'h-6 w-10 border-0 data-[state=checked]:bg-[#10b981] data-[state=unchecked]:bg-[#8f95a6] [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-4 [&>span]:data-[state=unchecked]:translate-x-0.5'

interface FormState {
  name: string
  date: string
  recurring: boolean
  is_active: boolean
}

const EMPTY: FormState = {
  name: '',
  date: '',
  recurring: false,
  is_active: true,
}

function StatusBadge({ active }: { active: boolean }) {
  const { t } = useTranslation('settings')
  return (
    <span
      className='inline-flex items-center justify-center rounded-lg px-1.5 py-1 text-xs font-medium leading-4'
      style={
        active
          ? { backgroundColor: '#D1FAE5', color: '#065F46' }
          : { backgroundColor: '#e6e6e6', color: '#1e1e1e' }
      }
    >
      {active ? t('common.active') : t('common.inactive')}
    </span>
  )
}

function RecurringBadge({ recurring }: { recurring: boolean }) {
  const { t } = useTranslation('settings')
  return (
    <span
      className='inline-flex items-center justify-center rounded-lg px-1.5 py-1 text-xs font-medium leading-4'
      style={
        recurring
          ? { backgroundColor: '#fff1ea', color: '#ff5a1f' }
          : { backgroundColor: '#f4f5f8', color: '#6c7285' }
      }
    >
      {recurring ? t('common.yes') : t('common.no')}
    </span>
  )
}

/** Day + month name, e.g. "1 January" (Figma holiday list). */
function formatHolidayDate(value: string | null | undefined): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-GB'
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
  }).format(d)
}

// 2.11.10 — Public holidays. Paginated CRUD list; name + date + recurring.
export function HolidaysPanel() {
  const { t } = useTranslation('settings')
  const { page, setPage, limit, setLimit, query } = useListParams()
  const { data, isLoading, isError } = usePublicHolidays(query)
  const create = useCreatePublicHoliday()
  const update = useUpdatePublicHoliday()
  const remove = useDeletePublicHoliday()

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<PublicHoliday | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [toDelete, setToDelete] = useState<PublicHoliday | null>(null)
  const [toToggle, setToToggle] = useState<PublicHoliday | null>(null)

  const rows = useMemo(() => data?.data ?? [], [data])
  const set = (patch: Partial<FormState>) => setForm(f => ({ ...f, ...patch }))

  function openCreate() {
    setEditing(null)
    setForm(EMPTY)
    setErrors({})
    setOpen(true)
  }

  function openEdit(h: PublicHoliday) {
    setEditing(h)
    setForm({
      name: h.name,
      date: h.date.slice(0, 10),
      recurring: h.recurring,
      is_active: h.is_active,
    })
    setErrors({})
    setOpen(true)
  }

  function validate(): PublicHolidayRequest | null {
    const next: Record<string, string> = {}
    if (!form.name.trim()) next.name = t('holidays.validation.nameRequired')
    else if (form.name.length > 100)
      next.name = t('holidays.validation.nameMax')
    if (!form.date) next.date = t('holidays.validation.dateRequired')
    setErrors(next)
    if (Object.keys(next).length) return null
    return {
      name: form.name.trim(),
      date: form.date,
      recurring: form.recurring,
      is_active: form.is_active,
    }
  }

  function submit() {
    const body = validate()
    if (!body) return
    const onSuccess = () => {
      toast({
        variant: 'success',
        title: editing
          ? t('holidays.toast.updated')
          : t('holidays.toast.created'),
      })
      setOpen(false)
    }
    const onError = (e: unknown) =>
      toast({ variant: 'destructive', title: getApiErrorMessage(e) })
    if (editing) update.mutate({ id: editing.id, body }, { onSuccess, onError })
    else create.mutate(body, { onSuccess, onError })
  }

  function confirmDelete() {
    if (!toDelete) return
    remove.mutate(toDelete.id, {
      onSuccess: () => {
        toast({ variant: 'success', title: t('holidays.toast.deleted') })
        setToDelete(null)
      },
      onError: e =>
        toast({ variant: 'destructive', title: getApiErrorMessage(e) }),
    })
  }

  function confirmToggle() {
    if (!toToggle) return
    const nextActive = !toToggle.is_active
    update.mutate(
      {
        id: toToggle.id,
        body: {
          name: toToggle.name,
          date: toToggle.date.slice(0, 10),
          recurring: toToggle.recurring,
          is_active: nextActive,
        },
      },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: nextActive
              ? t('holidays.toast.activated')
              : t('holidays.toast.deactivated'),
          })
          setToToggle(null)
        },
        onError: e =>
          toast({ variant: 'destructive', title: getApiErrorMessage(e) }),
      }
    )
  }

  return (
    <>
      <section className={cardClass}>
        <div className='flex flex-col gap-6 py-6'>
          <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
            <div className='min-w-0 flex-1'>
              <div className='flex min-w-0 items-center border-l-4 border-[#ff5a1f] px-6 py-1'>
                <h2 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
                  {t('holidays.title')}
                </h2>
              </div>
            </div>

            <Button
              className='mx-4 h-10 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 text-sm font-medium text-white shadow-none hover:bg-[#e6511c] sm:mx-5 lg:mx-6'
              onClick={openCreate}
            >
              <Plus className='mr-2 h-4 w-4' />
              {t('holidays.addHoliday')}
            </Button>
          </div>
        </div>

        <div className='px-4 pb-2 sm:px-5'>
          <div className='overflow-x-auto'>
            <table className='w-full min-w-[936px] caption-bottom text-sm'>
              <TableHeader>
                <TableRow className='border-0'>
                  <TableHead className={cn(thClass, 'w-[240px]')}>
                    {t('common.name')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[140px]')}>
                    {t('holidays.columns.date')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[180px] text-center')}>
                    {t('holidays.columns.recurring')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[180px] text-center')}>
                    {t('holidays.columns.status')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[160px]')}>
                    {t('holidays.columns.lastUpdated')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[136px]')}>
                    <div className='ml-auto text-right'>
                      {t('common.actions')}
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {isLoading &&
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={COLUMN_COUNT} className={tdClass}>
                        <Skeleton className='h-6 w-full' />
                      </TableCell>
                    </TableRow>
                  ))}

                {isError && !isLoading && (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className={cn(
                        tdClass,
                        'py-10 text-center text-destructive'
                      )}
                    >
                      {t('holidays.loadError')}
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && !isError && rows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className={cn(
                        tdClass,
                        'py-10 text-center text-muted-foreground'
                      )}
                    >
                      {t('holidays.empty')}
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading &&
                  !isError &&
                  rows.map(h => {
                    return (
                      <TableRow key={h.id} className='group border-0'>
                        <TableCell className={cn(tdClass)}>
                          <span className='text-[14px] font-medium leading-[1.4] text-[#171a20]'>
                            {h.name}
                          </span>
                        </TableCell>
                        <TableCell className={cn(tdClass)}>
                          <span className='whitespace-nowrap text-sm leading-[1.4] text-[#171a20]'>
                            {formatHolidayDate(h.date)}
                          </span>
                        </TableCell>
                        <TableCell className={cn(tdClass, 'text-center')}>
                          <RecurringBadge recurring={h.recurring} />
                        </TableCell>
                        <TableCell className={cn(tdClass, 'text-center')}>
                          <StatusBadge active={h.is_active} />
                        </TableCell>
                        <TableCell className={cn(tdClass)}>
                          <span className='whitespace-nowrap text-sm leading-[1.4] text-[#171a20]'>
                            {formatDate(h.updated_at)}
                          </span>
                        </TableCell>
                        <TableCell className={cn(tdClass)}>
                          <div className='flex items-center justify-end gap-2'>
                            <TruncatedTextTooltip
                              content={
                                h.is_active
                                  ? t('holidays.actions.deactivate')
                                  : t('holidays.actions.activate')
                              }
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={cn(
                                  actionBtnClass,
                                  accentActionBtnClass
                                )}
                                aria-label={
                                  h.is_active
                                    ? t('holidays.actions.deactivate')
                                    : t('holidays.actions.activate')
                                }
                                onClick={() => setToToggle(h)}
                              >
                                <Power className={cn('size-4')} />
                              </button>
                            </TruncatedTextTooltip>
                            <TruncatedTextTooltip
                              content={t('common:actions.edit')}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={cn(
                                  actionBtnClass,
                                  accentActionBtnClass
                                )}
                                aria-label={t('common:actions.edit')}
                                onClick={() => openEdit(h)}
                              >
                                <Pencil className='size-4' />
                              </button>
                            </TruncatedTextTooltip>
                            <TruncatedTextTooltip
                              content={t('common:actions.delete')}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={cn(
                                  actionBtnClass,
                                  destructiveActionBtnClass
                                )}
                                aria-label={t('common:actions.delete')}
                                onClick={() => setToDelete(h)}
                              >
                                <Trash2 className='size-4' />
                              </button>
                            </TruncatedTextTooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </table>
          </div>
        </div>

        <ListPagination
          metadata={data?.metadata}
          page={page}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
          className='px-4 sm:px-5'
          showShowingRange={false}
        />
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          side='right'
          showCloseButton={false}
          className={drawerClass}
        >
          <SettingsDrawerHeader
            title={
              editing
                ? t('holidays.dialog.editTitle')
                : t('holidays.dialog.addTitle')
            }
            closeDisabled={create.isPending || update.isPending}
            titleClassName='text-base'
          />

          <div className='flex flex-1 flex-col overflow-y-auto bg-[#f4f5f8]'>
            <div className='flex flex-1 flex-col gap-4 px-6 py-6'>
              <div className={cardClass}>
                <div className='border-b border-transparent px-0 pb-2 pt-4'>
                  <div className='flex items-center border-l-4 border-[#ff5a1f] px-4'>
                    <h3 className='font-heading text-sm font-semibold lowercase leading-[1.2] text-[#3d434e] first-letter:uppercase'>
                      INFORMATION
                    </h3>
                  </div>
                </div>

                <div
                  key={editing?.id ?? 'create'}
                  className='flex flex-col gap-6 px-4 pb-6 pt-4'
                >
                  <div className='flex flex-col gap-1'>
                    <Label htmlFor='h-name' className={fieldLabelClass}>
                      {t('holidays.fields.name')}
                      <span className='text-[#d63b33]'> *</span>
                    </Label>
                    <Input
                      id='h-name'
                      maxLength={100}
                      value={form.name}
                      onChange={e => {
                        set({ name: e.target.value })
                        setErrors(s => ({ ...s, name: '' }))
                      }}
                      placeholder={t('users.fields.enterHere')}
                      className={cn(
                        fieldInputClass,
                        errors.name && 'border border-destructive'
                      )}
                    />
                    {errors.name && (
                      <p className='text-sm text-destructive'>{errors.name}</p>
                    )}
                  </div>

                  <div className='flex flex-col gap-1'>
                    <Label htmlFor='h-date' className={fieldLabelClass}>
                      {t('holidays.fields.date')}
                      <span className='text-[#d63b33]'> *</span>
                    </Label>
                    <DatePicker
                      id='h-date'
                      value={form.date}
                      onChange={next => {
                        set({ date: next })
                        setErrors(s => ({ ...s, date: '' }))
                      }}
                      variant='field'
                      className={datePickerClass}
                      error={!!errors.date}
                    />
                    {errors.date && (
                      <p className='text-sm text-destructive'>{errors.date}</p>
                    )}
                  </div>

                  <div className='border-b border-[#e7e9ef] pb-3'>
                    <div className='flex items-center gap-3'>
                      <Label
                        htmlFor='h-rec'
                        className='flex-1 text-sm font-medium leading-[1.4] text-[#171a20]'
                      >
                        {t('holidays.fields.recurring')}
                      </Label>
                      <Switch
                        id='h-rec'
                        checked={form.recurring}
                        onCheckedChange={v => set({ recurring: v })}
                        className={switchClass}
                      />
                    </div>
                  </div>

                  <div className='border-b border-[#e7e9ef] pb-3'>
                    <div className='flex items-center gap-3'>
                      <Label
                        htmlFor='h-active'
                        className='flex-1 text-sm font-medium leading-[1.4] text-[#171a20]'
                      >
                        {t('holidays.fields.active')}
                      </Label>
                      <Switch
                        id='h-active'
                        checked={form.is_active}
                        onCheckedChange={v => set({ is_active: v })}
                        className={switchClass}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='tf-right-drawer-footer justify-end'>
            <div className='flex items-center justify-end gap-3'>
              <Button
                type='button'
                variant='outline'
                className={cn(drawerButtonOutlineClass, 'text-sm')}
                onClick={() => setOpen(false)}
              >
                {t('common:actions.cancel')}
              </Button>
              <Button
                type='button'
                className={cn(drawerButtonPrimaryClass, 'text-sm')}
                onClick={submit}
                disabled={create.isPending || update.isPending}
              >
                {t('common:actions.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={o => !remove.isPending && !o && setToDelete(null)}
        title={t('holidays.dialog.deleteTitle')}
        description={t('holidays.dialog.deleteMessage')}
        confirmLabel={t('holidays.dialog.deleteConfirm')}
        onConfirm={confirmDelete}
        busy={remove.isPending}
      />

      <Dialog
        open={!!toToggle}
        onOpenChange={o => !update.isPending && !o && setToToggle(null)}
      >
        <DialogContent className={confirmDialogClass}>
          <div className='flex flex-col gap-3 pr-8'>
            <DialogTitle className='font-heading text-2xl font-medium leading-[1.2] text-[#171a20]'>
              {toToggle?.is_active
                ? t('holidays.dialog.deactivateTitle')
                : t('holidays.dialog.activateTitle')}
            </DialogTitle>
            <p className='text-base font-normal leading-[1.4] text-[#171a20]'>
              {toToggle?.is_active
                ? t('holidays.dialog.deactivateMessage')
                : t('holidays.dialog.activateMessage')}
            </p>
          </div>
          <div className='flex items-center justify-end gap-4'>
            <Button
              type='button'
              variant='outline'
              className={drawerButtonOutlineClass}
              onClick={() => setToToggle(null)}
              disabled={update.isPending}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type='button'
              className={drawerButtonPrimaryClass}
              onClick={confirmToggle}
              disabled={update.isPending}
            >
              {t('common:actions.confirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default HolidaysPanel
